import * as THREE from 'three'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js'
import { DragControls } from 'three/examples/jsm/controls/DragControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import GUI from 'lil-gui'
import { Group, Tween, Easing } from '@tweenjs/tween.js'
import { initializeSetup, updatePanelContent } from './setup.js'
import { initEditor, handleEditorClick } from './editor.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { VignetteShader } from 'three/examples/jsm/shaders/VignetteShader.js'

// Global references for cleanup
let scene, plexusScene, camera, renderer, controls, dragControls, gui;
let composer, outlinePass; // Post-processing
let rafId; // Request Animation Frame ID
let activeConfig;
let hotspots = [];
let zoomDependentLights = [];
let loadedModel = null;
let beamMesh = null;
let plexusGroup = null;
let pulse1, pulse2, beamMaterial;
let tweenGroup // Will be set in loadScene
let raycaster, mouse;
let clock, mixer;

let isAnimating = false;
let activeSceneInstance = null; // Declare at module scope for disposeScene
let currentLoadId = 0;
let areHotspotsEnabled = true;
let isEditorMode = false;
let isCameraReset = false;
let isInteractionLocked = false;
let lockedDistance = 0;
let onMouseMoveListener = null;
let lastTouchTime = 0;
let preventResetOnPanelClose = false;

// Clean up previous scene
export function disposeScene() {
    if (rafId) window.cancelAnimationFrame(rafId);

    // Dispose GUI
    if (gui && gui.destroy) gui.destroy();

    // Dispose Mixer
    if (mixer) {
        mixer.stopAllAction();
        mixer = null;
    }

    // Helper to clear scene
    if (scene) {
        scene.traverse((child) => {
            if (child.isMesh) {
                child.geometry.dispose();
                if (Array.isArray(child.material)) {
                    child.material.forEach(m => {
                        m.dispose();
                        if (m.map) m.map.dispose();
                    });
                } else {
                    child.material.dispose();
                    if (child.material.map) child.material.map.dispose();
                }
            }
        });
        scene.clear();
    }

    if (plexusScene) {
        plexusScene.traverse((child) => {
            if (child.isMesh || child.isPoints || child.isLine) {
                child.geometry.dispose();
                if (child.material) child.material.dispose();
            }
        });
        plexusScene.clear();
    }

    // Reset Globals
    hotspots = [];
    loadedModel = null;
    beamMesh = null;
    isAnimating = false;
    isCameraReset = false;
    isInteractionLocked = false;
    lockedDistance = 0;
    preventResetOnPanelClose = false;
    // Cleanup Post-processing
    if (composer) {
        composer = null;
        outlinePass = null;
    }

    // Remove listeners
    window.removeEventListener('resize', onResize);
    window.removeEventListener('click', onHotspotClick);
    window.removeEventListener('touchstart', onHotspotClick);

    if (activeSceneInstance && activeSceneInstance.dispose) {
        activeSceneInstance.dispose();
        activeSceneInstance = null;
    }

    if (onMouseMoveListener) {
        window.removeEventListener('mousemove', onMouseMoveListener);
        onMouseMoveListener = null;
    }
}

// Separate Resize Handler so we can remove it
function onResize() {
    if (!camera || !renderer) return;
    const width = window.innerWidth;
    const height = window.innerHeight;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    if (composer) composer.setSize(width, height);
}

// Helper function to show the info panel
// Helper function to show the info panel
function showInfoPanel(title, subtitle, description) {
    // Format the title: remove underscores, capitalize words, keep L/R
    let formattedTitle = title
        .replace(/_/g, ' ')     // Replace underscores with spaces
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

    // Use shared setup function to populate content (ensures Learn More button works)
    updatePanelContent(null, {
        title: formattedTitle,
        description: description
    });

    // Show panel
    const panel = document.getElementById('info-panel');
    if (panel) panel.classList.add('active');
}

function onHotspotClick(event) {
    if (hotspots.length === 0 && !loadedModel) return;
    if (isAnimating) return;
    if (isInteractionLocked) return;

    // Handle Touch vs Mouse
    let clientX, clientY;
    if (event.changedTouches && event.changedTouches.length > 0) {
        // Touch Event
        clientX = event.changedTouches[0].clientX;
        clientY = event.changedTouches[0].clientY;
        lastTouchTime = Date.now();
    } else {
        // Mouse/Click Event
        if (Date.now() - lastTouchTime < 500) return; // Ignore ghost click
        clientX = event.clientX;
        clientY = event.clientY;
    }

    // 1. CHECK EDITOR MODE FIRST
    // Create a proxy event with valid coordinates for the editor if it's a touch event
    const effectiveEvent = (event.changedTouches && event.changedTouches.length > 0)
        ? { clientX, clientY, target: event.target, preventDefault: () => event.preventDefault(), stopPropagation: () => event.stopPropagation() }
        : event;

    if (handleEditorClick(effectiveEvent, loadedModel)) return;

    // 2. Normal Logic
    mouse.x = (clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(clientY / window.innerHeight) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);

    const objectsToTest = [...hotspots, loadedModel];
    // Filter nulls
    const validObjects = objectsToTest.filter(o => o);
    let intersects = raycaster.intersectObjects(validObjects, true);

    // Filter out ignored objects (e.g., Environment Cutaway)
    // Ensure activeConfig is defined/global. If not, try to find it.
    // Assuming activeConfig is a global defined elsewhere (checking...)
    if (typeof activeConfig === 'undefined') {
        console.warn("SceneManager: activeConfig is undefined in onHotspotClick!");
    } else if (activeConfig.ignoreCollisions) {
        // console.log("Filtering collisions with:", activeConfig.ignoreCollisions);
        intersects = intersects.filter(hit => {
            // Check direct object name or any parent name up the chain
            let current = hit.object;
            while (current) {
                // Check if current name contains any of the ignored strings
                const shouldIgnore = activeConfig.ignoreCollisions.some(ignoreStr => current.name.includes(ignoreStr));
                if (shouldIgnore) return false;

                if (current === loadedModel) break; // Stop at root
                current = current.parent;
            }
            return true;
        });
    }

    if (intersects.length > 0) {
        let clickedGroup = null;
        let firstModelHit = null;

        // Check for nested intersections (buttons inside panels) or top-level hotspots
        if (intersects.length > 0) {
            let clickedHotspot = null;
            let clickedButton = null;

            // Sort by distance to find the closest valid target
            intersects.sort((a, b) => a.distance - b.distance);

            for (const intersect of intersects) {
                const object = intersect.object;

                // Check if we hit a 3D Action Button inside a specific panel
                if (object.name === 'action_button_3d' || object.name === 'close_button_3d') {
                    if (object.parent && object.parent.visible) {
                        // Found a visible button click
                        clickedButton = object;
                        // Find the root hotspot group
                        let p = object.parent;
                        while (p && !hotspots.includes(p)) p = p.parent;
                        if (p) clickedHotspot = p;
                        break;
                    }
                }

                // Standard Hotspot Check
                let parent = object;
                while (parent) {
                    if (parent.isGroup && hotspots.includes(parent)) {
                        // Check if hidden or inactive
                        if (parent.visible && parent.userData.isActive && !parent.userData.isHiddenByClick) {
                            if (!clickedHotspot && !clickedButton) clickedHotspot = parent;
                        }
                        break;
                    }
                    parent = parent.parent;
                }

                if (clickedHotspot || clickedButton) break;
            }

            if (clickedButton && clickedHotspot) {
                if (clickedButton.name === 'close_button_3d') {
                    // RESET: Hide Panel, Show Icon
                    const panel = clickedHotspot.getObjectByName('info_panel_3d');
                    if (panel) panel.visible = false;
                    const icon = clickedHotspot.getObjectByName('plus_icon');
                    if (icon) icon.visible = true;
                } else {
                    // Case 1: Clicked the 'OPEN' button on the 3D panel
                    // Trigger standard action (zoom + slideout)
                    performHotspotAction(clickedHotspot);
                }

            } else if (clickedHotspot) {
                // Case 2: Clicked the Hotspot Icon itself

                if (clickedHotspot.userData.isPlusType) {
                    // Check for custom "Toggle Visibility" action first
                    if (clickedHotspot.userData.action === 'toggle_visibility' && clickedHotspot.userData.targetNodes) {
                        const nodesToToggle = clickedHotspot.userData.targetNodes;
                        if (loadedModel) {
                            loadedModel.traverse((child) => {
                                if (nodesToToggle.includes(child.name)) {
                                    child.visible = !child.visible;
                                }
                            });
                        }
                    } else {
                        // Default Behavior: OPEN PANEL, HIDE ICON

                        // Close of all others
                        hotspots.forEach(hp => {
                            if (hp !== clickedHotspot) {
                                const p = hp.getObjectByName('info_panel_3d');
                                if (p) p.visible = false;
                                const i = hp.getObjectByName('plus_icon');
                                if (i) i.visible = true;
                            }
                        });

                        const panel = clickedHotspot.getObjectByName('info_panel_3d');
                        if (panel) {
                            panel.visible = true;
                            panel.lookAt(camera.position); // Update orientation when showing
                        }
                        const icon = clickedHotspot.getObjectByName('plus_icon');
                        if (icon) icon.visible = false;
                    }
                } else {
                    // STANDARD HOTSPOT ACTION
                    if (!clickedHotspot.visible || !clickedHotspot.userData.isActive || clickedHotspot.userData.isHiddenByClick) return;
                    performHotspotAction(clickedHotspot);
                }
            }

            // NEW: Handle direct mesh clicks (only if enabled via config, e.g. for Scene 1)
            if (!clickedHotspot && !clickedButton && loadedModel && activeConfig && activeConfig.ui && activeConfig.ui.enableMeshSelection) {

                // Find the first mesh hit
                for (const intersect of intersects) {
                    if (intersect.object.isMesh) {
                        let current = intersect.object;
                        let foundName = null;

                        // Traverse up to find a significant name (or stop at Scene)
                        while (current && current !== scene && current !== loadedModel) {
                            if (current.name) {

                                // Check if this name matches any hotspot directly or via targetMesh
                                const match = SceneManager.checkNameAmongHotspots(current.name);
                                if (match) {
                                    foundName = current.name;
                                    break; // Found a valid hotspot target!
                                }
                            }
                            current = current.parent;
                        }

                        // If no hotspot match found, fallback to the mesh name itself (for direct interaction)
                        if (!foundName) foundName = intersect.object.name;

                        if (foundName) {

                            SceneManager.openPanelForPart(foundName);
                            break; // Stop after first valid hit
                        }
                    }
                }
            }
        }
    }
}

function performHotspotAction(clickedGroup) {

    // Hide the top instruction label on first hotspot click
    const topLabel = document.getElementById('top-instruction-label');
    if (topLabel) {
        topLabel.style.display = 'none';
    }

    // --- IMMEDIATE HIGHLIGHT LOGIC ---
    if (loadedModel && clickedGroup.userData && clickedGroup.userData.targetMesh) {
        const targetName = clickedGroup.userData.targetMesh;


        let targetObject = loadedModel.getObjectByName(targetName);

        if (!targetObject) {
            loadedModel.traverse((child) => {
                if (!targetObject && child.name.includes(targetName)) {
                    targetObject = child;
                }
            });

        }

        if (targetObject) {
            const meshesToOutline = [];
            targetObject.traverse((c) => {
                if (c.isMesh) meshesToOutline.push(c);
            });
            if (targetObject.isMesh) meshesToOutline.push(targetObject);

            if (outlinePass && meshesToOutline.length > 0) {
                // DISABLE FOR SCENE 2
                if (activeConfig && activeConfig.title === 'Operational View') {
                    // Skip
                } else {
                    outlinePass.selectedObjects = meshesToOutline;
                    const color = (activeConfig && activeConfig.theme && activeConfig.theme.outlineColor) || '#ffff00';
                    outlinePass.visibleEdgeColor.set(color);
                    outlinePass.pulsePeriod = 2; // Flash
                }
            }
        }
    }
    // ---------------------------------

    // 0. Check for Camera View (Zoom to specific view)
    // 0. Check for Camera View (Zoom to specific view)
    if (clickedGroup.userData.cameraView) {
        const view = clickedGroup.userData.cameraView;
        isAnimating = true;
        controls.enabled = false;

        let targetX, targetY, targetZ;

        if (view.target === 'auto') {
            // New Feature: Look at the object itself!
            // Use local position since hotspots are direct children of scene (World Pos = Local Pos)
            targetX = clickedGroup.position.x;
            targetY = clickedGroup.position.y;
            targetZ = clickedGroup.position.z;

        } else {
            targetX = view.target.x;
            targetY = view.target.y;
            targetZ = view.target.z;
        }

        const showPanel = () => {
            // Respect scene-level flag to suppress the side panel
            if (activeConfig && activeConfig.ui && activeConfig.ui.disableInfoPanel) return;

            // 1. Direct Data (Preferred for Labels)
            if (clickedGroup.userData.title || clickedGroup.userData.description) {
                updatePanelContent(null, clickedGroup.userData);
                document.getElementById('info-panel').classList.add('active');
                return;
            }

            // 2. Legacy Panel Key Lookup
            if (activeConfig.panels) {
                let panelKey = Object.keys(activeConfig.panels).find(key =>
                    key.toLowerCase() === clickedGroup.name.toLowerCase()
                );
                if (!panelKey && activeConfig.panels[clickedGroup.name]) panelKey = clickedGroup.name;

                if (panelKey) {
                    updatePanelContent(panelKey);
                    document.getElementById('info-panel').classList.add('active');
                }
            }

            // 3. Highlight Linked Mesh (Outline)
            if (loadedModel && clickedGroup.userData && clickedGroup.userData.targetMesh) {
                const targetName = clickedGroup.userData.targetMesh;


                let targetObject = loadedModel.getObjectByName(targetName);


                if (!targetObject) {
                    loadedModel.traverse((child) => {
                        if (!targetObject && child.name.includes(targetName)) {
                            targetObject = child;
                        }
                    });

                }

                if (targetObject) {
                    const meshesToOutline = [];
                    targetObject.traverse((c) => {
                        if (c.isMesh) meshesToOutline.push(c);
                    });
                    if (targetObject.isMesh) meshesToOutline.push(targetObject);

                    if (outlinePass && meshesToOutline.length > 0) {
                        // DISABLE FOR SCENE 2
                        if (activeConfig && activeConfig.title === 'Operational View') {
                            // Skip
                        } else {
                            outlinePass.selectedObjects = meshesToOutline;
                            const color = (activeConfig && activeConfig.theme && activeConfig.theme.outlineColor) || '#ffff00';
                            outlinePass.visibleEdgeColor.set(color);
                            outlinePass.pulsePeriod = 2; // Flash
                        }
                    }
                }
            }
        };

        const finishInteraction = () => {
            controls.enabled = true;
            isAnimating = false;
            if (activeConfig && activeConfig.lockInteraction) {
                isInteractionLocked = true;
                lockedDistance = controls.getDistance();
            }

            // Determine behavior from Config
            const hideOthers = (activeConfig.ui && activeConfig.ui.hideOthersOnSelect !== undefined)
                ? activeConfig.ui.hideOthersOnSelect
                : true; // Default to true (Current behavior)

            const hideSelf = (activeConfig.ui && activeConfig.ui.hideSelfOnSelect === true);

            // Update Visibility
            hotspots.forEach(hp => {
                if (hp !== clickedGroup) {
                    if (hideOthers) {
                        hp.visible = false;
                        hp.userData.isHiddenByClick = true;
                    } else {
                        // NON-HIDING BEHAVIOR: Ensure others are visible
                        hp.visible = true;
                        hp.userData.isHiddenByClick = false;

                        // Optional: Reset their internal state (e.g. show icon, hide label)
                        // This ensures that if we jump from A to B, A goes back to being a normal hotspot.
                        const icon = hp.getObjectByName('plus_icon') || hp.getObjectByName('hotspot_icon');
                        if (icon) icon.visible = true;

                        // Keep label logic as is or reset? Usually we want them to reset to default state.
                        // const label = hp.getObjectByName('hotspot_text_label');
                        // if (label) label.visible = false; 
                    }
                } else {
                    // Clicked Group
                    hp.userData.isHiddenByClick = true;

                    if (hideSelf) {
                        hp.visible = false;
                    } else {
                        hp.visible = true;
                        // Default logic: Hide Icon, Show Label
                        const icon = hp.getObjectByName('plus_icon') || hp.getObjectByName('hotspot_icon');
                        if (icon) icon.visible = false;

                        const label = hp.getObjectByName('hotspot_text_label');
                        if (label) label.visible = true;
                    }
                }
            });

            // SHOW PANEL NOW (instead of requiring second click)
            showPanel();
        };

        if (view.position === 'auto') {
            // Calculate a dynamic "Zoom In" position relative to the target
            // We want to move towards the target but stop at a certain distance
            const currentPos = camera.position.clone();
            const targetPos = new THREE.Vector3(targetX, targetY, targetZ);

            // Direction from target TO camera (to pull back along that line)
            const direction = new THREE.Vector3().subVectors(currentPos, targetPos).normalize();

            // USE CONFIGURABLE ZOOM DISTANCE
            const zoomDist = (activeConfig && activeConfig.model && activeConfig.model.controls && activeConfig.model.controls.zoomDistance !== undefined)
                ? activeConfig.model.controls.zoomDistance
                : 40; // Default fallback for Scene 3 type scale

            const finalCamPos = targetPos.clone().add(direction.multiplyScalar(zoomDist));

            new Tween(camera.position, tweenGroup)
                .to({ x: finalCamPos.x, y: finalCamPos.y, z: finalCamPos.z }, 1500)
                .easing(Easing.Cubic.InOut)
                .start();
        } else {
            new Tween(camera.position, tweenGroup)
                .to({ x: view.position.x, y: view.position.y, z: view.position.z }, 1500)
                .easing(Easing.Cubic.InOut)
                .start();
        }

        new Tween(controls.target, tweenGroup)
            .to({ x: targetX, y: targetY, z: targetZ }, 1500)
            .easing(Easing.Cubic.InOut)
            .onComplete(() => {
                // Check for Secondary View (Chained Animation)
                if (clickedGroup.userData.secondaryView) {
                    const sec = clickedGroup.userData.secondaryView;
                    const dur = sec.duration || 2000;
                    const delay = sec.delay || 0;


                    new Tween(camera.position, tweenGroup)
                        .to({ x: sec.position.x, y: sec.position.y, z: sec.position.z }, dur)
                        .easing(Easing.Cubic.InOut)
                        .delay(delay)
                        .start();

                    new Tween(controls.target, tweenGroup)
                        .to({ x: sec.target.x, y: sec.target.y, z: sec.target.z }, dur)
                        .easing(Easing.Cubic.InOut)
                        .delay(delay)
                        .onComplete(finishInteraction)
                        .start();
                } else {
                    finishInteraction();
                }
            })
            .start();
        return;
    }

    // 1. Direct Data (Preferred for Labels)
    if (clickedGroup.userData.title || clickedGroup.userData.description) {
        if (!activeConfig || !activeConfig.ui || !activeConfig.ui.disableInfoPanel) {
            updatePanelContent(null, clickedGroup.userData);
            document.getElementById('info-panel').classList.add('active');
        }
        return;
    }

    let panelKey = Object.keys(activeConfig.panels).find(key =>
        key.toLowerCase() === clickedGroup.name.toLowerCase()
    );

    if (!panelKey) {
        if (activeConfig.panels[clickedGroup.name]) panelKey = clickedGroup.name;
    }

    if (panelKey && (!activeConfig.ui || !activeConfig.ui.disableInfoPanel)) {
        updatePanelContent(panelKey);
        document.getElementById('info-panel').classList.add('active');
    }
}

export function zoomToView(newCameraPos, newTargetPos, duration = 2000) {
    if (!camera || !controls || isAnimating) return;
    isAnimating = true;
    controls.enabled = false;

    new Tween(camera.position, tweenGroup)
        .to(newCameraPos, duration)
        .easing(Easing.Quadratic.InOut)
        .start();

    new Tween(controls.target, tweenGroup)
        .to(newTargetPos, duration)
        .easing(Easing.Quadratic.InOut)
        .onComplete(() => {
            controls.enabled = true;
            isAnimating = false;
        })
        .start();
}


function zoomToTarget(target, distance = 60, onComplete = null) {
    const targetPosition = new THREE.Vector3();
    target.getWorldPosition(targetPosition);

    const cameraDirection = new THREE.Vector3();
    camera.getWorldDirection(cameraDirection);

    const newCameraPosition = new THREE.Vector3().addVectors(
        targetPosition,
        cameraDirection.multiplyScalar(-distance)
    );

    new Tween(camera.position, tweenGroup).to(newCameraPosition, 1000).easing(Easing.Quadratic.InOut).start();
    new Tween(controls.target, tweenGroup).to(targetPosition, 1000).easing(Easing.Quadratic.InOut).onComplete(() => {
        controls.enabled = true;
        controls.enableDamping = true;
        isAnimating = false;
        if (onComplete) onComplete();
    }).start();
}


export function loadScene(config, sceneModule, onLoaded) {
    // FORCE HIDE PANEL and clear potentially stale state
    const panel = document.getElementById('info-panel');
    if (panel) panel.classList.remove('active');

    if (typeof disposeScene === 'function') {
        disposeScene();
    }
    activeConfig = config;
    clock = new THREE.Clock();
    tweenGroup = new Group();
    raycaster = new THREE.Raycaster();
    mouse = new THREE.Vector2();

    const canvas = document.querySelector('canvas.webgl');
    scene = new THREE.Scene();
    plexusScene = new THREE.Scene();

    // Reset State
    areHotspotsEnabled = true;
    isInteractionLocked = false;
    isCameraReset = true; // Assume reset on load
    hotspots = [];

    // Debug
    if (config.settings.enableDebugPanel) {
        gui = new GUI();
    }

    // Sizes
    const sizes = { width: window.innerWidth, height: window.innerHeight };

    // Camera
    camera = new THREE.PerspectiveCamera(60, sizes.width / sizes.height, 1, 5000);
    camera.position.set(config.model.cameraPosition.x, config.model.cameraPosition.y, config.model.cameraPosition.z);
    scene.add(camera);

    // Controls
    controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.enablePan = true;
    controls.minDistance = config.model.controls.minDistance;
    controls.maxDistance = config.model.controls.maxDistance;

    // Set initial target if provided in config
    if (config.model.controls.target) {
        controls.target.set(
            config.model.controls.target.x,
            config.model.controls.target.y,
            config.model.controls.target.z
        );
    }

    if (config.model.controls.panLimits) {
        const minPan = new THREE.Vector3(config.model.controls.panLimits.min.x, config.model.controls.panLimits.min.y, config.model.controls.panLimits.min.z);
        const maxPan = new THREE.Vector3(config.model.controls.panLimits.max.x, config.model.controls.panLimits.max.y, config.model.controls.panLimits.max.z);
        controls.addEventListener('change', () => {
            const _v = new THREE.Vector3();
            _v.copy(controls.target);
            controls.target.clamp(minPan, maxPan);
            _v.sub(controls.target);
            camera.position.sub(_v);
        });
    }

    // Transform Controls
    const tControls = new TransformControls(camera, canvas);
    tControls.addEventListener('dragging-changed', (event) => {
        // Only toggle controls if NOT in strict editor mode (where we force disable)
        if (!isEditorMode) controls.enabled = !event.value;
    });
    scene.add(tControls);

    // Debug Lighting Config


    // Renderer Setup (Ensure Tone Mapping for HDRI)
    if (!renderer) {
        renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true, logarithmicDepthBuffer: true });
        renderer.setSize(sizes.width, sizes.height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    }
    // Always ensure shadows are enabled (must be set before first render, safe to set every load)
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.autoClear = false;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = config.toneMappingExposure !== undefined ? config.toneMappingExposure : 1.0;

    // Environment Map / HDRI Support
    if (config.environmentMap) {
        // Ensure renderer is ready
        const pmremGenerator = new THREE.PMREMGenerator(renderer);
        pmremGenerator.compileEquirectangularShader();

        const path = config.environmentMap;
        const isHDR = path.toLowerCase().endsWith('.hdr');

        const loader = isHDR ? new RGBELoader() : new THREE.TextureLoader();

        loader.load(path, (texture) => {
            texture.mapping = THREE.EquirectangularReflectionMapping;
            const envMap = pmremGenerator.fromEquirectangular(texture).texture;

            // Only set background if explicitly enabled or not defined (default true)
            // If the user wants to HIDE the image but KEEP the lighting, they set showEnvironmentBackground: false
            if (config.showEnvironmentBackground !== false) {
                scene.background = texture;
            } else {
                scene.background = null; // Ensure it's clear so gradients/colors show through
            }

            scene.environment = envMap; // Use processed for lighting

            // Apply Intensity if supported (Three.js r163+)
            if (config.environmentIntensity !== undefined) {
                scene.environmentIntensity = config.environmentIntensity;
            }

            texture.dispose();
            pmremGenerator.dispose();


        }, undefined, (err) => {
            console.error(`Failed to load environment map: ${path}`, err);
        });
    }

    // Lights
    const directionalLight = new THREE.DirectionalLight(config.lighting.directional.color, config.lighting.directional.intensity);
    if (config.lighting.directional.position) {
        directionalLight.position.set(
            config.lighting.directional.position.x,
            config.lighting.directional.position.y,
            config.lighting.directional.position.z
        );
    }
    // --- Shadow Setup ---
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 3000;
    const shadowCamSize = config.lighting.directional.shadowCamSize || 600;
    directionalLight.shadow.camera.left = -shadowCamSize;
    directionalLight.shadow.camera.right = shadowCamSize;
    directionalLight.shadow.camera.top = shadowCamSize;
    directionalLight.shadow.camera.bottom = -shadowCamSize;
    directionalLight.shadow.bias = -0.001;
    scene.add(directionalLight);

    // --- POST PROCESSING ---
    composer = new EffectComposer(renderer);

    zoomDependentLights = []; // Reset global tracking

    config.lighting.pointLights.forEach((lightConfig) => {
        const pointLight = new THREE.PointLight(lightConfig.color, lightConfig.intensity, lightConfig.distance);
        pointLight.position.set(lightConfig.position.x, lightConfig.position.y, lightConfig.position.z);
        scene.add(pointLight);

        if (lightConfig.visibleOnZoomOnly) {
            pointLight.visible = false;
            zoomDependentLights.push(pointLight);
        }
    });

    const hemisphereLight = new THREE.HemisphereLight(config.lighting.hemisphere.skyColor, config.lighting.hemisphere.groundColor, config.lighting.hemisphere.intensity);
    scene.add(hemisphereLight);

    // --- POST PROCESSING ---
    // Create a render target that matches the renderer's pixel ratio for crisp output
    const composerPixelRatio = renderer.getPixelRatio();
    const composerTarget = new THREE.WebGLRenderTarget(
        sizes.width * composerPixelRatio,
        sizes.height * composerPixelRatio,
        {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
            type: THREE.HalfFloatType
        }
    );
    composer = new EffectComposer(renderer, composerTarget);

    // 1. Background Pass (Plexus)
    const plexusPass = new RenderPass(plexusScene, camera);
    plexusPass.clear = true; // Clear screen for background
    composer.addPass(plexusPass);

    // 2. Main Scene Pass (Brain)
    const brainPass = new RenderPass(scene, camera);
    brainPass.clear = false; // Draw on top of plexus
    brainPass.clearDepth = true; // Clear depth buffer so brain renders cleanly on top
    composer.addPass(brainPass);

    // 2. Outline Pass
    outlinePass = new OutlinePass(new THREE.Vector2(window.innerWidth, window.innerHeight), scene, camera);
    outlinePass.edgeStrength = 4;
    outlinePass.edgeGlow = 0;
    outlinePass.edgeThickness = 1.0;
    outlinePass.downSampleRatio = 2; // Performance optimization
    outlinePass.pulsePeriod = 0; // Solid line
    outlinePass.visibleEdgeColor.set('#ffff00'); // Yellow
    outlinePass.hiddenEdgeColor.set('#190a05');
    outlinePass.hiddenEdgeColor.set('#190a05');
    composer.addPass(outlinePass);

    // 3. Output Pass — applies renderer.toneMapping + toneMappingExposure to the composed image
    const outputPass = new OutputPass();
    composer.addPass(outputPass);

    // 4. Vignette Pass (Custom Inline)
    if (config.postProcessing && config.postProcessing.vignette && config.postProcessing.vignette.enabled) {
        const CustomVignetteShader = {
            uniforms: {
                "tDiffuse": { value: null },
                "offset": { value: 1.0 },
                "darkness": { value: 1.5 }
            },
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
                }
            `,
            fragmentShader: `
                uniform float offset;
                uniform float darkness;
                uniform sampler2D tDiffuse;
                varying vec2 vUv;
                void main() {
                    vec4 texel = texture2D( tDiffuse, vUv );
                    vec2 uv = ( vUv - vec2( 0.5 ) ) * vec2( offset );
                    gl_FragColor = vec4( mix( texel.rgb, vec3( 0.0 ), dot( uv, uv ) * darkness ), texel.a );
                }
            `
        };

        const vignettePass = new ShaderPass(CustomVignetteShader);
        vignettePass.uniforms["offset"].value = config.postProcessing.vignette.offset || 1.0;
        vignettePass.uniforms["darkness"].value = config.postProcessing.vignette.darkness || 1.5;
        composer.addPass(vignettePass);

    }

    // 4. Output Pass (Gamma/Tone mapping)
    // 5. Vignette Pass (Second instance to ensure it applies after OutputPass)
    if (config.postProcessing && config.postProcessing.vignette && config.postProcessing.vignette.enabled) {
        const CustomVignetteShader = {
            uniforms: {
                "tDiffuse": { value: null },
                "offset": { value: 1.0 },
                "darkness": { value: 1.5 }
            },
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
                }
            `,
            fragmentShader: `
                uniform float offset;
                uniform float darkness;
                uniform sampler2D tDiffuse;
                varying vec2 vUv;
                void main() {
                    vec4 texel = texture2D( tDiffuse, vUv );
                    vec2 uv = ( vUv - vec2( 0.5 ) ) * vec2( offset );
                    gl_FragColor = vec4( mix( texel.rgb, vec3( 0.0 ), dot( uv, uv ) * darkness ), texel.a );
                }
            `
        };
        const vignettePass2 = new ShaderPass(CustomVignetteShader);
        vignettePass2.uniforms["offset"].value = config.postProcessing.vignette.offset || 1.0;
        vignettePass2.uniforms["darkness"].value = config.postProcessing.vignette.darkness || 1.5;
        composer.addPass(vignettePass2);

    }
    // If sceneModule is provided, run its init
    activeSceneInstance = null;
    if (sceneModule && sceneModule.init) {
        activeSceneInstance = sceneModule.init(scene, camera, renderer, controls, gui, config);
    }

    // --- BACKGROUND HANDLING ---
    plexusGroup = new THREE.Group();
    plexusScene.add(plexusGroup);

    let updateBackground = null;

    if (config.settings.background === 'cubeGrid') {
        const color = 0x0088ff; // Tech Blue
        const size = 2000;
        const divisions = 40;

        // 3D Grid Construction
        // Floor
        const gridHelper = new THREE.GridHelper(size, divisions, color, 0x002244);
        gridHelper.position.y = -500;
        gridHelper.material.transparent = true;
        gridHelper.material.opacity = 0.5;
        plexusGroup.add(gridHelper);

        // Ceiling
        const gridHelperTop = new THREE.GridHelper(size, divisions, color, 0x002244);
        gridHelperTop.position.y = 500;
        gridHelperTop.material.transparent = true;
        gridHelperTop.material.opacity = 0.5;
        plexusGroup.add(gridHelperTop);

        // Add some random floating cubes for "depth
        const boxGeo = new THREE.BoxGeometry(50, 50, 50);
        const boxMat = new THREE.LineBasicMaterial({ color: color, transparent: true, opacity: 0.2 });
        const boxEdge = new THREE.EdgesGeometry(boxGeo);

        for (let i = 0; i < 30; i++) {
            const mesh = new THREE.LineSegments(boxEdge, boxMat);
            mesh.position.set(
                (Math.random() - 0.5) * 1500,
                (Math.random() - 0.5) * 800,
                (Math.random() - 0.5) * 1500
            );
            mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
            plexusGroup.add(mesh);
        }

    } else if (config.settings.showPlexus !== false) {
        // Setup Plexus
        const plexusCount = 100; const plexusRange = 800; const particlesData = [];
        const particleGeometry = new THREE.BufferGeometry();
        const particlePositions = new Float32Array(plexusCount * 3);
        const lineGeometry = new THREE.BufferGeometry();
        const linePositions = new Float32Array(plexusCount * plexusCount * 3);

        for (let i = 0; i < plexusCount; i++) {
            const x = (Math.random() - 0.5) * plexusRange; const y = (Math.random() - 0.5) * plexusRange; const z = (Math.random() - 0.5) * plexusRange;
            particlePositions[i * 3] = x; particlePositions[i * 3 + 1] = y; particlePositions[i * 3 + 2] = z;
            particlesData.push({ velocity: new THREE.Vector3((Math.random() - 0.5) * 0.5, (Math.random() - 0.5) * 0.5, (Math.random() - 0.5) * 0.5) });
        }

        particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
        const particleMaterial = new THREE.PointsMaterial({
            color: 0xFFFFFF,
            size: 3,
            transparent: true,
            opacity: 0.6,
            depthWrite: false,
            depthTest: true
        });
        const plexusPoints = new THREE.Points(particleGeometry, particleMaterial);
        plexusPoints.renderOrder = -100;
        plexusGroup.add(plexusPoints);

        lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
        const lineMaterial = new THREE.LineBasicMaterial({
            color: 0x134c97,
            transparent: true,
            opacity: 0.2,
            linewidth: 1,
            depthWrite: false,
            depthTest: true
        });
        const plexusLines = new THREE.LineSegments(lineGeometry, lineMaterial);
        plexusLines.renderOrder = -100;
        plexusGroup.add(plexusLines);

        updateBackground = function () {
            let vertexIndex = 0; const connectionDistance = 150;
            const positions = plexusPoints.geometry.attributes.position.array;
            const linePosAttr = plexusLines.geometry.attributes.position;

            for (let i = 0; i < plexusCount; i++) {
                positions[i * 3] += particlesData[i].velocity.x; positions[i * 3 + 1] += particlesData[i].velocity.y; positions[i * 3 + 2] += particlesData[i].velocity.z;
                if (Math.abs(positions[i * 3]) > plexusRange / 2) particlesData[i].velocity.x *= -1;
                if (Math.abs(positions[i * 3 + 1]) > plexusRange / 2) particlesData[i].velocity.y *= -1;
                if (Math.abs(positions[i * 3 + 2]) > plexusRange / 2) particlesData[i].velocity.z *= -1;
            }
            plexusPoints.geometry.attributes.position.needsUpdate = true;

            for (let i = 0; i < plexusCount; i++) {
                for (let j = i + 1; j < plexusCount; j++) {
                    const dx = positions[i * 3] - positions[j * 3]; const dy = positions[i * 3 + 1] - positions[j * 3 + 1]; const dz = positions[i * 3 + 2] - positions[j * 3 + 2];
                    const distSq = dx * dx + dy * dy + dz * dz;
                    if (distSq < connectionDistance * connectionDistance) {
                        linePosAttr.array[vertexIndex++] = positions[i * 3]; linePosAttr.array[vertexIndex++] = positions[i * 3 + 1]; linePosAttr.array[vertexIndex++] = positions[i * 3 + 2];
                        linePosAttr.array[vertexIndex++] = positions[j * 3]; linePosAttr.array[vertexIndex++] = positions[j * 3 + 1]; linePosAttr.array[vertexIndex++] = positions[j * 3 + 2];
                    }
                }
            }
            plexusLines.geometry.setDrawRange(0, vertexIndex / 3);
            linePosAttr.needsUpdate = true;
        };
    }

    // --- GENERIC LOADING LOGIC ---
    const sceneLoadId = ++currentLoadId; // Capture current ID
    // If config has model path, load it.
    const gltfLoader = new GLTFLoader();
    if (config.model && config.model.path) {
        gltfLoader.load(
            config.model.path,
            (gltf) => {
                if (sceneLoadId !== currentLoadId) {

                    return;
                }

                loadedModel = gltf.scene;

                // Handle Animations
                if (gltf.animations && gltf.animations.length > 0) {
                    mixer = new THREE.AnimationMixer(loadedModel);
                    gltf.animations.forEach((clip) => {
                        mixer.clipAction(clip).play();
                    });
                }

                // Apply adjustments
                if (config.model.scale) {
                    const s = config.model.scale;
                    loadedModel.scale.set(s, s, s);
                }

                if (config.model.autoCenter) {
                    const box = new THREE.Box3().setFromObject(loadedModel);
                    const center = box.getCenter(new THREE.Vector3());
                    loadedModel.position.x += (loadedModel.position.x - center.x);
                    loadedModel.position.y += (loadedModel.position.y - center.y);
                    loadedModel.position.z += (loadedModel.position.z - center.z);
                }

                if (config.model.positionOffset) {
                    loadedModel.position.x += config.model.positionOffset.x || 0;
                    loadedModel.position.y += config.model.positionOffset.y || 0;
                    loadedModel.position.z += config.model.positionOffset.z || 0;
                }

                // Force material override or applying config settings
                const matConfig = config.model.materialProperties;
                const partMaterials = config.model.partMaterials || {};
                const groupMaterials = config.model.groupMaterials || {};

                // Get max anisotropy for texture sharpening
                const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

                // Helper: find which groupMaterials entry (if any) applies to a mesh
                const findGroupProps = (mesh) => {
                    let current = mesh.parent;
                    while (current && current !== loadedModel.parent) {
                        if (current.name && groupMaterials[current.name]) {
                            return groupMaterials[current.name];
                        }
                        current = current.parent;
                    }
                    return null;
                };

                loadedModel.traverse((child) => {
                    if (child.isMesh) {
                        // CLONE MATERIAL to ensure unique instances for highlighting
                        child.material = child.material.clone();
                        child.material.side = THREE.DoubleSide; // Ensure insides are lit

                        // Enable shadows
                        child.castShadow = true;
                        child.receiveShadow = true;

                        // Sharpen textures with max anisotropic filtering
                        const textureMaps = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap'];
                        textureMaps.forEach(mapName => {
                            if (child.material[mapName]) {
                                child.material[mapName].anisotropy = maxAnisotropy;
                            }
                        });

                        // Determine properties: Part > Group > Global > Default
                        const partProps = partMaterials[child.name];
                        const groupProps = findGroupProps(child);

                        // 1. Color Override - Only apply if explicitly specified for this part,
                        //    or if the original material color is pure black (likely unset/default)
                        if (partProps && partProps.color) {
                            child.material.color.set(partProps.color);
                        } else if (groupProps && groupProps.color) {
                            child.material.color.set(groupProps.color);
                        } else if (matConfig && matConfig.color) {
                            // Only override if original is pure black (default/unset in GLTF)
                            if (child.material.color.getHexString() === '000000') {
                                child.material.color.set(matConfig.color);
                            }
                            // Otherwise preserve the GLTF's own material color
                        } else if (child.material.color.getHexString() === '000000') {
                            child.material.color.setHex(0xaaaaaa);
                        }

                        // 2. Metalness & Roughness
                        // Default Fallback
                        let finalMetalness = 0.1;
                        let finalRoughness = 0.7;

                        // Global Config Override
                        if (matConfig) {
                            if (matConfig.metalness !== undefined) finalMetalness = matConfig.metalness;
                            if (matConfig.roughness !== undefined) finalRoughness = matConfig.roughness;
                        }

                        // Group Override (between Global and Part)
                        if (groupProps) {
                            if (groupProps.metalness !== undefined) finalMetalness = groupProps.metalness;
                            if (groupProps.roughness !== undefined) finalRoughness = groupProps.roughness;
                        }

                        // Part Specific Override (highest priority)
                        if (partProps) {
                            if (partProps.metalness !== undefined) finalMetalness = partProps.metalness;
                            if (partProps.roughness !== undefined) finalRoughness = partProps.roughness;
                        }

                        child.material.metalness = finalMetalness;
                        child.material.roughness = finalRoughness;

                        // Boost env map response for physically-captured materials
                        if (child.material.envMapIntensity !== undefined) {
                            child.material.envMapIntensity = config.model.envMapIntensity !== undefined
                                ? config.model.envMapIntensity
                                : 1.2;
                        }

                        child.material.needsUpdate = true;
                    }
                });

                scene.add(loadedModel);

                // Log model info
                const box = new THREE.Box3().setFromObject(loadedModel);
                const size = box.getSize(new THREE.Vector3());

                console.log(`Model Position: x:${loadedModel.position.x}, y:${loadedModel.position.y}, z:${loadedModel.position.z}`);


                // DEBUG: Log FULL Hierarchy to find Group names
                loadedModel.traverse(c => {
                    let type = c.isMesh ? "Mesh" : (c.isGroup ? "Group" : "Object");
                });


                hotspots = [];
                if (config.hotspots) {
                    config.hotspots.forEach(data => {
                        createSingleHotspot(data);
                    });
                }
                initEditor(raycaster, camera, scene, hotspots, renderer, controls, tControls);

                // DONE LOADING
                if (onLoaded) onLoaded();
            },
            (xhr) => {
                const percent = (xhr.loaded / xhr.total * 100);
                const bar = document.getElementById('loading-bar-progress');
                if (bar) bar.style.width = percent + '%';
            },
            (error) => {
                console.error('An error happened loading the model:', error);
                if (onLoaded) onLoaded();
            }
        );
    } else {
        // No Model to load
        setTimeout(() => {
            if (onLoaded) onLoaded();
        }, 100);
    }

    // --- EVENTS & TICK ---

    // HOVER STATE LOGIC
    let hoveredHotspot = null;
    const originalColors = new Map(); // Store original colors to restore them accurately

    onMouseMoveListener = (event) => {
        // Only for Scene 2 and Scene 3
        if (!activeConfig || (activeConfig.title !== 'Operational View' && activeConfig.title !== 'Product Explorer')) return;

        // Calculate mouse position
        const rect = renderer.domElement.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        raycaster.setFromCamera(mouse, camera);

        // Raycast against all hotspot groups
        const intersects = raycaster.intersectObjects(hotspots, true);

        if (intersects.length > 0) {
            // Find the first object that is part of a hotspot
            const hit = intersects.find(i => i.object.name === 'hotspot_icon' || i.object.name === 'plus_icon');

            if (hit) {
                const group = hit.object.parent;
                const icon = hit.object;

                if (hoveredHotspot !== group) {
                    // Reset previous
                    if (hoveredHotspot) {
                        const prevIcon = hoveredHotspot.getObjectByName('hotspot_icon') || hoveredHotspot.getObjectByName('plus_icon');
                        if (prevIcon) {
                            if (originalColors.has(prevIcon.uuid)) {
                                prevIcon.material.color.setHex(originalColors.get(prevIcon.uuid));
                            } else {
                                prevIcon.material.color.setHex(0xffffff);
                            }
                        }
                    }

                    // Set new
                    hoveredHotspot = group;
                    if (!originalColors.has(icon.uuid)) {
                        originalColors.set(icon.uuid, icon.material.color.getHex());
                    }

                    // Hover Color - Scene-specific behavior
                    if (activeConfig && activeConfig.title === 'Product Explorer') {
                        // Scene 3: Keep green background, change plus icon to grey
                        updatePlusIconColor(icon, '#2ea91b', '#444444');
                    } else {
                        // Scene 2 and others: Change entire icon color
                        let hoverColor = 0xADD8E6; // Default / Scene 2 Blue
                        icon.material.color.setHex(hoverColor);
                    }
                    document.body.style.cursor = 'pointer';
                }
            } else {
                // Hit something else (not icon)
                if (hoveredHotspot) {
                    const prevIcon = hoveredHotspot.getObjectByName('hotspot_icon') || hoveredHotspot.getObjectByName('plus_icon');
                    if (prevIcon) {
                        if (activeConfig && activeConfig.title === 'Product Explorer') {
                            // Scene 3: Restore green background with white plus
                            updatePlusIconColor(prevIcon, '#2ea91b', '#ffffff');
                        } else if (originalColors.has(prevIcon.uuid)) {
                            prevIcon.material.color.setHex(originalColors.get(prevIcon.uuid));
                        } else {
                            prevIcon.material.color.setHex(0xffffff);
                        }
                    }
                    hoveredHotspot = null;
                    document.body.style.cursor = 'default';
                }
            }
        } else {
            // No hit
            if (hoveredHotspot) {
                const prevIcon = hoveredHotspot.getObjectByName('hotspot_icon') || hoveredHotspot.getObjectByName('plus_icon');
                if (prevIcon) {
                    if (activeConfig && activeConfig.title === 'Product Explorer') {
                        // Scene 3: Restore green background with white plus
                        updatePlusIconColor(prevIcon, '#2ea91b', '#ffffff');
                    } else if (originalColors.has(prevIcon.uuid)) {
                        prevIcon.material.color.setHex(originalColors.get(prevIcon.uuid));
                    } else {
                        prevIcon.material.color.setHex(0xffffff);
                    }
                }
                hoveredHotspot = null;
                document.body.style.cursor = 'default';
            }
        }
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('click', onHotspotClick);
    window.addEventListener('touchstart', onHotspotClick, { passive: false });
    window.addEventListener('mousemove', onMouseMoveListener);

    // Debug helper: Double click to log coordinates (Using Capture Phase to ensure triggering)
    function onDebugDoubleClick(event) {
        if (camera && controls) {

        }
    }
    window.addEventListener('dblclick', onDebugDoubleClick, true);


    const resetToDefaultView = () => {
        // if (!isInteractionLocked) return; // Removed to allow manual forced reset


        isInteractionLocked = false;
        lockedDistance = 0;

        // 1. Reset Camera & Controls
        // Use 'config' from closure directly to ensure validity
        if (config && config.model) {

            const camPos = config.model.cameraPosition;
            if (camPos) {

                new Tween(camera.position, tweenGroup)
                    .to({ x: camPos.x, y: camPos.y, z: camPos.z }, 2000)
                    .easing(Easing.Cubic.InOut)
                    .start();
            }

            const target = (config.model.controls && config.model.controls.target)
                ? config.model.controls.target
                : { x: 0, y: 0, z: 0 };



            new Tween(controls.target, tweenGroup)
                .to({ x: target.x, y: target.y, z: target.z }, 2000)
                .easing(Easing.Cubic.InOut)
                .start();
        }

        // 2. Reset Hotspots Visibility
        hotspots.forEach(hp => {
            hp.visible = true;
            hp.userData.isHiddenByClick = false;

            // Reset labels visibility (Show them by default on reset)
            const label = hp.getObjectByName('hotspot_text_label');
            if (label) label.visible = true;

            // Show icon
            const icon = hp.getObjectByName('plus_icon') || hp.getObjectByName('hotspot_icon');
            if (icon) icon.visible = true;

            // Hide 3D panel
            const panel = hp.getObjectByName('info_panel_3d');
            if (panel) panel.visible = false;
        });

        // 3. Update UI (Close panel if open)
        const uiPanel = document.getElementById('info-panel');
        if (uiPanel) uiPanel.classList.remove('active');

        // 4. Clear Outlines
        if (outlinePass) {
            outlinePass.selectedObjects = [];
        }
    };

    // Wire up the external accessor REPEATEDLY to ensure it's set
    if (!window.SceneManager) window.SceneManager = {};
    window.SceneManager.onPanelClosed = resetToDefaultView;
    window.SceneManager.resetView = resetToDefaultView;
    window.SceneManager.zoomToView = zoomToView;
    window.SceneManager.getHotspots = () => hotspots;

    // ── DEBUG: Double-click anywhere to log camera position + target ──────────
    const _debugCamLog = () => {
        if (!camera || !controls) return;
        const p = camera.position;
        const t = controls.target;
        console.log(
            `%c[CAMERA] pos: {x:${p.x.toFixed(0)}, y:${p.y.toFixed(0)}, z:${p.z.toFixed(0)}} | target: {x:${t.x.toFixed(0)}, y:${t.y.toFixed(0)}, z:${t.z.toFixed(0)}}`,
            'color:#ffad66; font-weight:bold; background:#111; padding:2px 6px; border-radius:4px;'
        );
    };
    window.addEventListener('dblclick', _debugCamLog);
    // ──────────────────────────────────────────────────────────────────────────

    const tick = () => {
        const delta = clock.getDelta();
        const elapsedTime = clock.getElapsedTime();

        tweenGroup.update();
        controls.update();

        // Auto-Reset Check
        if (isInteractionLocked && activeConfig && activeConfig.lockInteraction) {
            // If distance > lockedDistance + 50 (robust buffer), trigger reset
            if (controls.getDistance() > lockedDistance + 50) {
                resetToDefaultView();
            }
        }

        if (plexusGroup && plexusGroup.visible && updateBackground) updateBackground();
        updateHotspotsVisibility();

        // Auto-flip callout labels based on screen-space position
        if (camera) {
            const _ndcVec = new THREE.Vector3();
            hotspots.forEach(hp => {
                hp.children.forEach(child => {
                    if (!child.userData.isCallout) return;
                    // Project hotspot world position to NDC
                    _ndcVec.setFromMatrixPosition(hp.matrixWorld);
                    _ndcVec.project(camera);
                    // NDC x: -1 = left edge, +1 = right edge
                    const onLeft = _ndcVec.x < 0;
                    if (onLeft !== child.userData._lastFlipped) {
                        child.userData._lastFlipped = onLeft;
                        updateCalloutSpriteFlip(
                            child,
                            child.userData.calloutText,
                            child.userData.calloutColor,
                            onLeft
                        );
                    }
                });
            });
        }

        // PULSE ANIMATION (Scene 2 and Scene 3)
        if (activeConfig && (activeConfig.title === 'Operational View' || activeConfig.title === 'Product Explorer')) {
            const time = elapsedTime * 2; // Slower speed
            const scaleFactor = 1 + Math.sin(time) * 0.15; // Oscillate between 0.85 and 1.15 (relative to base)

            hotspots.forEach(hp => {
                // Only animate if visible and NOT currently hovered (keeps hover state clean)
                // Or we can animate hover state too. Let's just animate scale.
                const icon = hp.getObjectByName('plus_icon') || hp.getObjectByName('hotspot_icon');

                if (icon && icon.visible) {
                    // Check base scale (store it if not stored)
                    if (!icon.userData.baseScale) {
                        icon.userData.baseScale = icon.scale.clone();
                    }

                    // Apply pulse
                    icon.scale.copy(icon.userData.baseScale).multiplyScalar(scaleFactor);
                }
            });
        }

        if (activeSceneInstance && activeSceneInstance.update) {
            activeSceneInstance.update(delta, elapsedTime); // Pass delta!
        }

        if (mixer) {
            mixer.update(delta);
        }

        // Render
        if (composer) {
            composer.render();
        } else {
            renderer.clear();
            renderer.render(scene, camera);
        }

        rafId = window.requestAnimationFrame(tick);
    }
    tick();



    // --- HELPERS (Scoped inside loadScene) ---
    function createTextSprite(message, tintColor = '#001f3f', textStyles = {}) {
        const LABEL_OFFSET_X = 2; const TEXT_PADDING = 15; const TRIANGLE_HEIGHT = 0; const BORDER_RADIUS = 4;
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const font = textStyles.font || '32px sans-serif';
        const textColor = textStyles.color || 'white';
        context.font = font;
        const textMetrics = context.measureText(message);
        const textWidth = textMetrics.width;
        const fontMetrics = context.measureText('M');
        const textHeight = fontMetrics.actualBoundingBoxAscent + fontMetrics.actualBoundingBoxDescent;
        const boxWidth = textWidth + (TEXT_PADDING * 2);
        const boxHeight = textHeight + (TEXT_PADDING * 2);
        const totalCanvasHeight = boxHeight + TRIANGLE_HEIGHT;
        canvas.width = boxWidth; canvas.height = totalCanvasHeight;
        context.font = font; context.fillStyle = tintColor; context.strokeStyle = tintColor;
        const boxX = 0; const boxY = 0;
        context.beginPath();
        if (context.roundRect) {
            context.roundRect(boxX, boxY, boxWidth, boxHeight, BORDER_RADIUS);
        } else {
            context.rect(boxX, boxY, boxWidth, boxHeight); // Fallback
        }
        context.fill();

        context.fillStyle = textColor; context.textAlign = 'center'; context.textBaseline = 'middle';
        context.fillText(message, boxWidth / 2, boxHeight / 2);
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false });
        const sprite = new THREE.Sprite(spriteMaterial);
        const scale = 0.5;
        sprite.scale.set(scale * (canvas.width / canvas.height), scale, 1);
        sprite.userData.totalCanvasHeight = totalCanvasHeight;
        return sprite;
    }

    function createCalloutSprite(text, colorStr = '#ffad66', flipped = false) {
        const canvas = document.createElement('canvas');
        canvas.width = 1024;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');

        ctx.font = 'bold 36px "Arial", sans-serif';
        const textWidth = ctx.measureText(text).width;

        ctx.strokeStyle = colorStr;
        ctx.lineWidth = 6;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';

        let startX, startY, bendX, bendY, endX;

        if (!flipped) {
            // Default: anchor bottom-left, arrow goes up-right, text on the right
            startX = 60;
            startY = 400;
            bendX  = 140;
            bendY  = 320;
            endX   = bendX + textWidth + 40;
        } else {
            // Flipped: anchor bottom-right, arrow goes up-left, text on the left
            startX = canvas.width - 60;
            startY = 400;
            bendX  = canvas.width - 140;
            bendY  = 320;
            endX   = bendX - textWidth - 40; // extends left
        }

        // Main L-shaped line
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(bendX, bendY);
        ctx.lineTo(endX, bendY);
        ctx.stroke();

        // Arrow-head ticks at startX/startY
        const dx  = bendX - startX;
        const dy  = bendY - startY;
        const ang = Math.atan2(dy, dx);

        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(startX + 25 * Math.cos(ang - Math.PI / 8), startY + 25 * Math.sin(ang - Math.PI / 8));
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(startX + 25 * Math.cos(ang + Math.PI / 8), startY + 25 * Math.sin(ang + Math.PI / 8));
        ctx.stroke();

        // End dot
        const dotX = !flipped ? endX + 12 : endX - 12;
        ctx.beginPath();
        ctx.arc(dotX, bendY, 8, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0)';
        ctx.fill();
        ctx.stroke();

        // Text background box
        ctx.fillStyle = colorStr;
        ctx.beginPath();
        const boxW = textWidth + 20;
        const boxH = 50;
        const boxX = !flipped ? bendX + 10 : endX - 10;
        const boxY = bendY - 55;
        if (ctx.roundRect) {
            ctx.roundRect(boxX, boxY, boxW, boxH, 6);
        } else {
            ctx.rect(boxX, boxY, boxW, boxH);
        }
        ctx.fill();

        // Label text
        ctx.fillStyle = '#222222';
        ctx.textBaseline = 'bottom';
        if (!flipped) {
            ctx.textAlign = 'left';
            ctx.fillText(text, bendX + 20, bendY - 14);
        } else {
            ctx.textAlign = 'right';
            ctx.fillText(text, bendX - 20, bendY - 14);
        }

        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;

        const spriteMaterial = new THREE.SpriteMaterial({ map: texture, depthTest: false, transparent: true });
        const sprite = new THREE.Sprite(spriteMaterial);

        // Anchor sprite so the arrow tip (startX/startY) sits at the hotspot position
        sprite.center.set(
            startX / canvas.width,
            (canvas.height - startY) / canvas.height
        );
        sprite.scale.set(150, 75, 1);
        sprite.name = 'hotspot_icon';

        return sprite;
    }

    // Helper: rebuild a callout sprite texture in-place (avoids replacing the object)
    function updateCalloutSpriteFlip(sprite, text, colorStr, flipped) {
        const newSprite = createCalloutSprite(text, colorStr, flipped);
        sprite.material.map.dispose();
        sprite.material.map = newSprite.material.map;
        sprite.material.needsUpdate = true;
        sprite.center.copy(newSprite.center);
        sprite.material.map.needsUpdate = true;
        // Clean up temp material
        newSprite.material.dispose();
    }


    function createDotSprite(color = '#001f3f', iconType = 'dot') {
        const canvas = document.createElement('canvas');
        canvas.width = 64; canvas.height = 64;
        const ctx = canvas.getContext('2d');

        if (iconType === 'plus') {
            // Draw rounded square background
            ctx.fillStyle = color;
            ctx.beginPath();
            if (ctx.roundRect) {
                ctx.roundRect(8, 8, 48, 48, 8); // rounded corners
            } else {
                ctx.rect(8, 8, 48, 48); // fallback
            }
            ctx.fill();

            // Draw white Plus Icon
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(28, 16, 8, 32); // Vertical bar
            ctx.fillRect(16, 28, 32, 8); // Horizontal bar
        } else {
            // Draw Dot (Original)
            // Outer Circle
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(32, 32, 30, 0, Math.PI * 2);
            ctx.fill();

            // Inner White Circle
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(32, 32, 12, 0, Math.PI * 2);
            ctx.fill();
        }

        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false });
        const sprite = new THREE.Sprite(material);
        sprite.scale.set(10, 10, 1); // Adjust scale as needed
        return sprite;
    }

    function updatePlusIconColor(sprite, backgroundColor, iconColor) {
        // Redraw the canvas with new colors
        const canvas = document.createElement('canvas');
        canvas.width = 64; canvas.height = 64;
        const ctx = canvas.getContext('2d');

        // Draw rounded square background
        ctx.fillStyle = backgroundColor;
        ctx.beginPath();
        if (ctx.roundRect) {
            ctx.roundRect(8, 8, 48, 48, 8);
        } else {
            ctx.rect(8, 8, 48, 48);
        }
        ctx.fill();

        // Draw Plus Icon with specified color
        ctx.fillStyle = iconColor;
        ctx.fillRect(28, 16, 8, 32); // Vertical bar
        ctx.fillRect(16, 28, 32, 8); // Horizontal bar

        // Update the sprite's texture
        const texture = new THREE.CanvasTexture(canvas);
        sprite.material.map = texture;
        sprite.material.needsUpdate = true;
    }

    function createPlusIconSprite() {
        // Reuse Dot Sprite for Plus icons to enforce Blue Dot theme globally
        const sprite = createDotSprite();
        // Rename for compatibility with existing logic that looks for 'plus_icon'
        sprite.name = 'plus_icon';
        return sprite;
    }

    function create3DInfoPanel(data) {
        // Group to hold panel elements
        const panelGroup = new THREE.Group();
        panelGroup.name = 'info_panel_3d';
        panelGroup.visible = false; // Hidden by default

        const width = 100;
        const height = 60;
        const padding = 5;

        // Background
        const bgGeo = new THREE.PlaneGeometry(width, height);
        const bgMat = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide, transparent: true, opacity: 0.9, depthTest: false }); // Always on top
        const background = new THREE.Mesh(bgGeo, bgMat);
        background.position.set(0, 0, 0);
        panelGroup.add(background);

        // Text (Title) - Simple Canvas Texture
        const canvas = document.createElement('canvas');
        canvas.width = 512; canvas.height = 256;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#333';
        ctx.font = 'bold 40px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(data.text || "Info Panel", 256, 80);
        ctx.font = '30px sans-serif';
        ctx.fillText("Click to learn more", 256, 140);

        const txtTex = new THREE.CanvasTexture(canvas);
        const txtMat = new THREE.MeshBasicMaterial({ map: txtTex, transparent: true, depthTest: false });
        const textMesh = new THREE.Mesh(new THREE.PlaneGeometry(width, height / 2), txtMat);
        textMesh.position.set(0, 10, 0.1); // Slightly in front
        panelGroup.add(textMesh);

        // Interaction Button (Visual representation)
        const btnCanvas = document.createElement('canvas');
        btnCanvas.width = 256; btnCanvas.height = 64;
        const btnCtx = btnCanvas.getContext('2d');
        btnCtx.fillStyle = '#0e577b';
        btnCtx.roundRect(0, 0, 256, 64, 10);
        btnCtx.fill();
        btnCtx.fillStyle = '#fff';
        btnCtx.font = 'bold 30px sans-serif';
        btnCtx.textAlign = 'center';
        btnCtx.textBaseline = 'middle';
        btnCtx.fillText("OPEN", 128, 32);

        const btnTex = new THREE.CanvasTexture(btnCanvas);
        const btnMat = new THREE.MeshBasicMaterial({ map: btnTex, transparent: true, depthTest: false });
        const btnMesh = new THREE.Mesh(new THREE.PlaneGeometry(40, 10), btnMat);
        btnMesh.position.set(0, -15, 0.2);
        btnMesh.name = 'action_button_3d'; // Key for raycasting!
        panelGroup.add(btnMesh);

        // Close Button (X)
        const closeCanvas = document.createElement('canvas');
        closeCanvas.width = 64; closeCanvas.height = 64;
        const closeCtx = closeCanvas.getContext('2d');
        closeCtx.fillStyle = '#cc0000'; // Red bg
        closeCtx.beginPath(); closeCtx.arc(32, 32, 30, 0, Math.PI * 2); closeCtx.fill();
        closeCtx.strokeStyle = '#fff'; closeCtx.lineWidth = 5;
        closeCtx.beginPath(); closeCtx.moveTo(20, 20); closeCtx.lineTo(44, 44); closeCtx.moveTo(44, 20); closeCtx.lineTo(20, 44); closeCtx.stroke();

        const closeTex = new THREE.CanvasTexture(closeCanvas);
        const closeMat = new THREE.MeshBasicMaterial({ map: closeTex, transparent: true, depthTest: false });
        const closeMesh = new THREE.Mesh(new THREE.PlaneGeometry(10, 10), closeMat);
        closeMesh.position.set(45, 25, 0.2); // Top Right corner
        closeMesh.name = 'close_button_3d';
        panelGroup.add(closeMesh);

        return panelGroup;
    }

    function createSingleHotspot(data) {
        const hotspotGroup = new THREE.Group();
        hotspotGroup.name = data.name;

        if (data.position === 'auto') {
            // Auto-position based on mesh center
            const targetName = data.targetMesh || data.name;
            let targetMesh = null;
            if (loadedModel) {
                targetMesh = loadedModel.getObjectByName(targetName);
                if (!targetMesh) {
                    // Fallback: Partial Search (Case Insensitive)
                    loadedModel.traverse((child) => {
                        if (!targetMesh && child.isMesh && child.name.toLowerCase().includes(targetName.toLowerCase())) {
                            targetMesh = child;
                        }
                    });
                }
            }

            if (targetMesh) {
                const box = new THREE.Box3().setFromObject(targetMesh);
                const center = new THREE.Vector3();
                box.getCenter(center);

                hotspotGroup.position.copy(center);

                // Apply Offset if provided
                if (data.offset) {
                    hotspotGroup.position.x += data.offset.x || 0;
                    hotspotGroup.position.y += data.offset.y || 0;
                    hotspotGroup.position.z += data.offset.z || 0;
                }
            } else {
                console.warn(`Hotspot auto-pos failed for: ${targetName}`);
                hotspotGroup.position.set(0, 0, 0);
            }
        } else {
            const pos = data.position;
            hotspotGroup.position.set(pos.x, pos.y, pos.z);
        }

        if (data.type === 'plus') {
            // --- create PLUS TYPE hotspot ---
            const plusSprite = createPlusIconSprite();
            hotspotGroup.add(plusSprite);

            // Create 3D Info Panel (Hidden by default)
            const panel = create3DInfoPanel(data);
            panel.position.set(20, 20, 0); // Offset nearby
            panel.lookAt(camera.position); // Look at camera
            hotspotGroup.add(panel);

            // Allow group to be raycasted
            hotspotGroup.userData.isPlusType = true;

        } else if (data.type === 'icon') {
            // Check for scene-specific hotspot style
            const useGreenPlus = activeConfig && activeConfig.hotspotStyle === 'plusLabel';
            const useCallout = activeConfig && activeConfig.hotspotStyle === 'callout';

            if (useCallout) {
                const colorStr = data.color || (activeConfig && activeConfig.hotspotColor) || '#ffca99';
                const calloutSprite = createCalloutSprite(data.text || data.title || "LABEL", colorStr, false);
                calloutSprite.renderOrder = 9999;
                // Tag for auto-flip logic
                calloutSprite.userData.isCallout    = true;
                calloutSprite.userData.calloutText  = data.text || data.title || "LABEL";
                calloutSprite.userData.calloutColor = colorStr;
                calloutSprite.userData._lastFlipped = false;
                hotspotGroup.add(calloutSprite);
            } else if (useGreenPlus) {
                // Scene 3 Style: Green Plus Icon with Label
                const darkGreen = '#2ea91b';
                const iconSprite = createDotSprite(darkGreen, 'plus');
                iconSprite.scale.set(20, 20, 1);
                iconSprite.position.set(0, 0, 0);
                iconSprite.name = 'hotspot_icon';
                iconSprite.renderOrder = 9999; // Always on top
                hotspotGroup.add(iconSprite);

                // Text Label positioned to the right with dark green background
                /* DISABLED TEXT LABELS
                if (data.text) {
                    const textSprite = createTextSprite(data.text, darkGreen);
                    textSprite.scale.multiplyScalar(25);
                    textSprite.position.set(25, 0, 0); // Position to the right of plus
                    textSprite.name = 'hotspot_text_label';
                    textSprite.renderOrder = 9999; // Always on top
                    textSprite.visible = false; // Hidden by default, shown on click
                    hotspotGroup.add(textSprite);
                }
                */
            } else {
                // Default Style: Blue Dot Icon or Custom Image
                let iconSprite;
                if (activeConfig && activeConfig.hotspotIconPath) {
                    const texture = new THREE.TextureLoader().load(activeConfig.hotspotIconPath);
                    const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false });
                    iconSprite = new THREE.Sprite(mat);
                    const pScale = config.settings.hotspotScale || 20;
                    iconSprite.scale.set(pScale, pScale, 1);
                } else {
                    iconSprite = createDotSprite();
                    iconSprite.scale.set(15, 15, 1);
                }
                
                iconSprite.position.set(0, 8, 0);
                iconSprite.name = 'hotspot_icon';
                iconSprite.renderOrder = 9999; // Always on top
                hotspotGroup.add(iconSprite);

                // Text Label above dot
                if (data.text) {
                    const textSprite = createTextSprite(data.text);
                    textSprite.scale.multiplyScalar(30);
                    textSprite.position.set(0, 25, 0); // Position above the dot
                    textSprite.name = 'hotspot_text_label';
                    textSprite.renderOrder = 9999; // Always on top
                    hotspotGroup.add(textSprite);
                }
            }

        } else {
            // --- create DEFAULT TYPE hotspot (Icon + Line + Text) ---
            const iconPath = config.hotspotIconPath || config.settings.hotspotIconPath;
            const hotspotTexture = new THREE.TextureLoader().load(iconPath);
            const hotspotMaterial = new THREE.SpriteMaterial({ map: hotspotTexture, transparent: true, opacity: 0.8, depthTest: false, depthWrite: false });
            const hotspotSprite = new THREE.Sprite(hotspotMaterial);
            hotspotSprite.scale.set(config.settings.hotspotScale, config.settings.hotspotScale, 1);
            hotspotSprite.name = 'hotspot_icon';
            const tintColor = data.color || config.theme.defaultHotspotColor;
            const textSprite = createTextSprite(data.text, tintColor);
            textSprite.scale.multiplyScalar(20);
            textSprite.position.set(25, 10, 0);
            textSprite.name = 'hotspot_text_label';

            hotspotGroup.add(hotspotSprite);
            hotspotGroup.add(textSprite);
        }

        scene.add(hotspotGroup);
        hotspots.push(hotspotGroup);
        hotspotGroup.name = data.name;
        hotspotGroup.userData.isActive = true;
        hotspotGroup.userData.visibleRange = data.visibleRange;
        // COPY CUSTOM ACTION DATA
        hotspotGroup.userData.action = data.action;
        hotspotGroup.userData.targetNodes = data.targetNodes;
        hotspotGroup.userData.targetMesh = data.targetMesh;
        hotspotGroup.userData.title = data.title;
        hotspotGroup.userData.description = data.description;
        hotspotGroup.userData.text = data.text;
        hotspotGroup.userData.cameraView = data.cameraView;
        hotspotGroup.userData.secondaryView = data.secondaryView;
        hotspotGroup.visible = !(activeConfig && activeConfig.hideHotspotsOnLoad);
    } // End of createSingleHotspot

    // End of loadScene
}

function updateHotspotsVisibility() {
    if (!camera) return;

    if (isEditorMode) {
        hotspots.forEach(hp => hp.visible = true);
        return;
    }

    // Distance to center (assuming model is at 0,0,0)
    const dist = camera.position.distanceTo(new THREE.Vector3(0, 0, 0));

    // --- ZOOM DEPENDENT LIGHTS LOGIC ---
    // User requested "turns on when it starts to zoom in"
    if (zoomDependentLights) {
        const ZOOM_THRESHOLD = 600;
        const isZoomingIn = dist < ZOOM_THRESHOLD;
        zoomDependentLights.forEach(l => l.visible = isZoomingIn);
    }

    if (!areHotspotsEnabled) {
        hotspots.forEach(hp => {
            if (!hp.userData.isHiddenByClick) hp.visible = false;
        });
        return;
    }

    // Check for "Reset" condition (Zooming far out resets hidden hotspots)
    // Check for "Reset" condition (Zooming far out resets hidden hotspots)
    const RESET_DISTANCE = 800;

    if (dist > RESET_DISTANCE) {
        // ONE-SHOT RESET LOGIC
        if (!isCameraReset) {
            isCameraReset = true;

            // 1. Show all hotspots
            hotspots.forEach(hp => {
                if (hp.userData.isHiddenByClick) {
                    hp.userData.isHiddenByClick = false;
                    hp.visible = true;
                }
            });

            // 2. Gently return to home view if we aren't already animating
            /* DISABLED BY USER REQUEST (Step 554)
            if (!isAnimating && activeConfig) {
                // Prioritize 'model.cameraPosition' (Scene 2 style) then 'initialCamera' (Scene 1 style)
                const startPos = activeConfig.model?.cameraPosition || activeConfig.initialCamera?.position;

                if (startPos) {
                    new Tween(camera.position, tweenGroup)
                        .to({ x: startPos.x, y: startPos.y, z: startPos.z }, 2000)
                        .easing(Easing.Cubic.Out)
                        .start();
                }

                new Tween(controls.target, tweenGroup)
                    .to({ x: 0, y: 0, z: 0 }, 2000)
                    .easing(Easing.Cubic.Out)
                    .start();
            }
            */
        }
    } else {
        // Reset flag when we come back close, allowing the reset to trigger again next time we zoom out
        if (isCameraReset) {
            isCameraReset = false;
        }
    }

    hotspots.forEach(hp => {
        if (hp.userData.visibleRange) {
            const { min, max } = hp.userData.visibleRange;
            // If max is not defined, assume infinite
            const maxVal = max !== undefined ? max : Infinity;
            const inRange = dist >= min && dist <= maxVal;

            // Only touch visibility if it's not hidden by interaction state
            if (!hp.userData.isHiddenByClick) {
                hp.visible = inRange;
            }
        }
    });
}

// Function to reset view (Hoisted helper)
function resetCameraView() {
    // Prioritize scene-specific reset if available
    if (activeSceneInstance && activeSceneInstance.reset) {
        activeSceneInstance.reset();
        return;
    }

    // Fallback to generic reset logic
    if (activeConfig) {
        const startPos = activeConfig.model?.cameraPosition || activeConfig.initialCamera?.position;
        if (startPos) {
            isAnimating = true; // Block other interactions
            const TWEEN_DURATION = 2000;

            new Tween(camera.position, tweenGroup)
                .to({ x: startPos.x, y: startPos.y, z: startPos.z }, TWEEN_DURATION)
                .easing(Easing.Cubic.Out)
                .onComplete(() => { isAnimating = false; })
                .start();

            // Fix: Respect the configured target (e.g. Scene 3 offset) instead of forcing 0,0,0
            const targetPoint = activeConfig.model?.controls?.target || { x: 0, y: 0, z: 0 };
            new Tween(controls.target, tweenGroup)
                .to({ x: targetPoint.x, y: targetPoint.y, z: targetPoint.z }, TWEEN_DURATION)
                .easing(Easing.Cubic.Out)
                .start();
        }
    }
}

// Expose for debugging/console
window.SceneManager = {
    loadScene,
    get scene() { return scene; },
    get camera() { return camera; },
    get controls() { return controls; },
    get loadedModel() { return loadedModel; },
    get activeConfig() { return activeConfig; },
    getHotspots: () => hotspots,
    setOutlineObjects: (objects) => {
        if (activeConfig && activeConfig.title === 'Operational View') {
            if (outlinePass) outlinePass.selectedObjects = [];
            return;
        }
        if (outlinePass) outlinePass.selectedObjects = objects;
    },
    resetView: resetCameraView,
    onPanelClosed: () => {
        // console.log("onPanelClosed called. Prevent Reset:", preventResetOnPanelClose);
        if (preventResetOnPanelClose) {
            if (outlinePass) outlinePass.selectedObjects = [];
            return;
        }
        resetCameraView();
    },
    setPreventResetOnPanelClose: (val) => {
        preventResetOnPanelClose = val;
    },
    enableHotspots: (enabled) => {
        areHotspotsEnabled = enabled;
        if (zoomDependentLights) {
            zoomDependentLights.forEach(l => l.visible = enabled);
        }
        updateHotspotsVisibility(); // Ensure correct state application
    },
    setEditorMode: (enabled) => {
        isEditorMode = enabled;
        updateHotspotsVisibility();
    },
    checkNameAmongHotspots: (name) => {
        if (!name || hotspots.length === 0) return null;
        // Check Name
        let match = hotspots.find(h => h.name === name);
        // Check Target Mesh Exact
        if (!match) match = hotspots.find(h => h.userData.targetMesh === name);
        // Check Target Mesh Partial
        if (!match) match = hotspots.find(h => h.userData.targetMesh && name.includes(h.userData.targetMesh));
        return match;
    },
    openPanelForPart: (partName, options = {}) => {

        if (!partName) return;

        let target = null;

        // 1. Try to find a matching hotspot
        // 1. Try to find a matching hotspot
        if (hotspots.length > 0) {
            // Priority 1: Exact Name Match
            target = hotspots.find(h => h.name === partName);

            // Priority 2: Config 'targetMesh' Match
            if (!target) {
                target = hotspots.find(h => h.userData.targetMesh === partName);
            }

            // Priority 3: Fuzzy / Partial Match (e.g. "Shovel_01" -> "Shovel")
            if (!target) {
                target = hotspots.find(h => {
                    // Check if mesh name contains targetMesh (e.g. "Excavator_Shovel" contains "Shovel")
                    if (h.userData.targetMesh && partName.includes(h.userData.targetMesh)) return true;
                    // Check if targetMesh contains mesh name (rare, but possible)
                    if (h.userData.targetMesh && h.userData.targetMesh.includes(partName)) return true;
                    return false;
                });
            }

            // Priority 4: Old fallback (Legacy)
            if (!target) target = hotspots.find(h => h.name === partName + "_Hub");
        }

        if (target) {
            // MATCH FOUND: Use Hotspot Logic


            // IF onlyPanel is requested (e.g. Hover in Scene 1), skip Zoom/Animation
            if (options.onlyPanel) {

                if (target.userData && (target.userData.title || target.userData.description)) {
                    updatePanelContent(null, target.userData);
                    document.getElementById('info-panel').classList.add('active');
                }
                return; // Stop here
            }



            // TRIGGER ACTION (Zoom)
            performHotspotAction(target);

            // SHOW PANEL
            if (target.userData && (target.userData.title || target.userData.description)) {
                updatePanelContent(null, target.userData);
                document.getElementById('info-panel').classList.add('active');
            } else {
                console.warn("SceneManager: Hotspot found but missing title/desc.");
            }

            // HIGHLIGHT MESH (Even for Hotspots)
            if (loadedModel && target.userData && target.userData.targetMesh) {
                const targetName = target.userData.targetMesh;
                // Find the object first (Mesh or Group)
                let targetObject = null;

                // 1. Exact Match
                targetObject = loadedModel.getObjectByName(targetName);

                // 2. Partial Match (if exact not found)
                if (!targetObject) {
                    loadedModel.traverse((child) => {
                        if (!targetObject && child.name.includes(targetName)) {
                            targetObject = child;
                        }
                    });
                }

                if (targetObject) {
                    const meshesToOutline = [];
                    // Collect all meshes inside this object (inclusive)
                    targetObject.traverse((c) => {
                        if (c.isMesh) meshesToOutline.push(c);
                    });

                    if (outlinePass && meshesToOutline.length > 0) {
                        // DISABLE HIGHLIGHT FOR SCENE 2
                        if (activeConfig && activeConfig.title === 'Operational View') {
                            outlinePass.selectedObjects = [];
                            return;
                        }

                        outlinePass.selectedObjects = meshesToOutline;
                        // Determine color based on activeConfig (Scene 3 should be Blue)
                        const color = (activeConfig && activeConfig.theme && activeConfig.theme.outlineColor) || '#ffff00';
                        outlinePass.visibleEdgeColor.set(color);
                        outlinePass.pulsePeriod = 2;
                    }
                }
            }
        } else {
            // MATCH NOT FOUND: Direct Mesh Interaction

            // CHECK: If config requires mapping to a hotspot, ABORT here
            if (activeConfig && activeConfig.ui && activeConfig.ui.onlyHotspots) {
                // EXCEPTION: If data is explicitly passed (e.g. from Scene 1), allow it
                if (!options.data) return;
            }

            if (options.data) {
                // Use provided data
                showInfoPanel(options.data.title || partName, "", options.data.description || options.data.text || "");
            } else {
                // Default Fallback
                showInfoPanel(partName, "", `Information about ${partName}`);
            }

            // Try to highlight the mesh AND zoom to it
            if (loadedModel) {
                let foundMesh = null;
                loadedModel.traverse((child) => {
                    if (child.isMesh && child.name === partName) {
                        foundMesh = child;
                        if (outlinePass) {
                            if (activeConfig && activeConfig.title === 'Operational View') {
                                outlinePass.selectedObjects = [];
                                return; // Skip setting objects
                            }
                            outlinePass.selectedObjects = [child];
                            // Set Flash Effect
                            const color = (activeConfig && activeConfig.theme && activeConfig.theme.outlineColor) || '#ffff00';
                            outlinePass.visibleEdgeColor.set(color);
                            outlinePass.pulsePeriod = 2; // Flash frequency
                        }
                    }
                });

                if (foundMesh) {
                    // Zoom to the mesh!
                    if (!options.onlyPanel) {
                        // Use a default distance appropriate for viewing parts (e.g., 50 units)
                        const zoomDist = (activeConfig && activeConfig.model && activeConfig.model.controls && activeConfig.model.controls.zoomDistance !== undefined)
                            ? activeConfig.model.controls.zoomDistance
                            : 60; // Keep old default for Scene 1

                        // Inline Zoom Logic (Reliable)


                        const box = new THREE.Box3().setFromObject(foundMesh);
                        const center = new THREE.Vector3();
                        box.getCenter(center);

                        const currentPos = camera.position.clone();
                        const direction = new THREE.Vector3().subVectors(currentPos, center).normalize();
                        const finalCamPos = center.clone().add(direction.multiplyScalar(zoomDist));

                        isAnimating = true;
                        new Tween(camera.position, tweenGroup)
                            .to({ x: finalCamPos.x, y: finalCamPos.y, z: finalCamPos.z }, 1500)
                            .easing(Easing.Cubic.InOut)
                            .start();

                        new Tween(controls.target, tweenGroup)
                            .to({ x: center.x, y: center.y, z: center.z }, 1500)
                            .easing(Easing.Cubic.InOut)
                            .onComplete(() => { isAnimating = false; })
                            .start();

                    } else {

                    }
                }
            }
        }
    }
};



